import React, { useMemo, useState } from 'react';
import { Lock, Check, X, Wrench } from 'lucide-react';
import { GbNodeSheet } from './GbNodeSheet';
import { GbCityMap } from './GbCityMap';
import { GbTank, GbCrest } from './GbTank';
import { GbFit } from './GbFit';

// as 6 regiões da Floresta Encantada — 4 fases em cada
const CIDADES = [
  { id: 1, nome: 'Vila da Abóbora', lore: 'Onde tudo começa', art: '/gb/world/c1.webp', x: 13, y: 56 },
  { id: 2, nome: 'Bosque dos Sussurros', lore: 'Árvores que observam', art: '/gb/world/c2c.webp', x: 29, y: 38 },
  { id: 3, nome: 'Ponte dos Espectros', lore: 'Dois andares de medo', art: '/gb/world/c3.webp', x: 45, y: 54 },
  { id: 4, nome: 'Pântano das Bruxas', lore: 'Ilhas sobre o lodo', art: '/gb/world/c4.webp', x: 60, y: 34 },
  { id: 5, nome: 'Cemitério Antigo', lore: 'Terra rara, abismo farto', art: '/gb/world/c5.webp', x: 75, y: 56 },
  { id: 6, nome: 'Castelo do Conde', lore: 'O fim da estrada', art: '/gb/world/c6.webp', x: 90, y: 36 },
];

const CityPin = ({ cidade, fases, atual, starsCity, onOpen }) => {
  const locked = fases.every((f) => f.node > atual);
  const done = fases.every((f) => f.node < atual);
  const aqui = !locked && !done;
  const dominada = starsCity >= 12;
  return (
    <button
      type="button"
      onClick={() => !locked && onOpen(cidade)}
      disabled={locked}
      data-testid={`gb-city-${cidade.id}`}
      style={{ left: `${cidade.x}%`, top: `${cidade.y}%` }}
      className={`group absolute z-10 h-[74px] w-[74px] -translate-x-1/2 -translate-y-1/2 transition-transform duration-300 sm:h-[104px] sm:w-[104px]
        ${locked ? 'cursor-not-allowed' : 'hover:scale-110 active:scale-95'}`}
    >
      {aqui && <span className="absolute inset-0 -m-3 animate-ping rounded-full bg-orange-400/25" />}
      <img src={cidade.art} alt={cidade.nome} draggable="false"
        className={`absolute inset-0 h-full w-full object-contain drop-shadow-[0_6px_14px_rgba(0,0,0,.65)]
          ${locked ? 'brightness-[.35] saturate-0' : ''}`} />
      {locked && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Lock className="h-5 w-5 text-purple-200/80" />
        </span>
      )}
      {dominada ? (
        <span className="absolute -right-1 -top-1" data-testid={`gb-city-crest-${cidade.id}`}>
          <GbCrest className="h-7 w-7" />
        </span>
      ) : done ? (
        <span className="absolute -right-1 -top-1 rounded-full border border-emerald-300/70 bg-emerald-600 p-[3px]">
          <Check className="h-3 w-3 text-white" />
        </span>
      ) : null}
      <div className={`absolute left-1/2 top-full w-max -translate-x-1/2 rounded-full border px-2 py-[2px] text-center backdrop-blur-sm
        ${aqui ? 'border-orange-400/80 bg-orange-950/80' : 'border-purple-700/60 bg-[#120a20]/85'}`}>
        <p className={`whitespace-nowrap text-[9px] font-black uppercase tracking-wide sm:text-[11px]
          ${locked ? 'text-purple-400/70' : aqui ? 'text-orange-200' : 'text-purple-100'}`}>
          {cidade.nome}
        </p>
        <p className="whitespace-nowrap text-[8px] text-amber-300/90 sm:text-[10px]">
          Fases {fases[0].node}–{fases[fases.length - 1].node} · ★ {starsCity}/12
        </p>
      </div>
    </button>
  );
};

export const GbWorldMap = ({ nodes, progress, diamonds, onPlay, starting, recem, onClose,
  travado, avisoTrava }) => {
  const [cidade, setCidade] = useState(null);
  const [sel, setSel] = useState(null);
  const cleared = useMemo(() => new Set((progress?.cleared || []).map(Number)), [progress]);
  const stars = progress?.stars || {};
  const paid = progress?.rewards || {};
  const lista = useMemo(() => [...(nodes || [])].sort((a, b) => a.node - b.node), [nodes]);
  const atual = Math.min(Math.max(1, lista.length || 1), Number(progress?.node || 1));
  const grupos = useMemo(
    () => CIDADES.map((c, i) => ({ cidade: c, fases: lista.slice(i * 4, i * 4 + 4) })).filter((g) => g.fases.length),
    [lista]);
  const totalStars = useMemo(
    () => Object.values(stars).reduce((s, v) => s + Number(v || 0), 0), [stars]);
  const starsDe = (fases) => fases.reduce((s, f) => s + Number(stars[String(f.node)] || 0), 0);
  const bloqueada = (n) => Number(n.node) > atual;

  // trilha pontilhada entre cidades: cada trecho só aparece quando a região é alcançada
  const atualIdx = Math.max(0, CIDADES.findIndex((c) => c.id === (grupos.find((g) => !g.completo)?.cidade.id || CIDADES[CIDADES.length - 1].id)));
  const focoMapa = Math.min(0.95, Math.max(0.05, CIDADES[atualIdx].x / 100));
  const trechos = useMemo(() => {
    const out = [];
    for (let i = 1; i < CIDADES.length; i++) {
      const p = CIDADES[i - 1], q = CIDADES[i];
      const mx = (p.x + q.x) / 2;
      const alvo = i * 4 + 1;           // 1ª fase da cidade de destino
      out.push({
        d: `M ${p.x} ${p.y} C ${mx} ${p.y}, ${mx} ${q.y}, ${q.x} ${q.y}`,
        aberto: atual >= alvo,
        proximo: atual >= alvo - 4 && atual < alvo,
        novo: Number(recem || 0) + 1 === alvo,
      });
    }
    return out;
  }, [atual, recem]);

  return (
    <div data-testid="gb-world-map" className="absolute inset-0 overflow-hidden">
      <div className="pwa-safe-top absolute inset-x-0 top-0 z-20 flex items-center gap-3 bg-gradient-to-b from-[#0b0712] via-[#0b0712cc] to-transparent px-4 py-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-black tracking-wide text-orange-300 md:text-lg">FLORESTA ENCANTADA</h2>
          <p className="text-[11px] text-purple-300/70">
            Fase {atual} de {lista.length} · {cleared.size} concluídas ·{' '}
            <span className="text-amber-300">★ {totalStars}/{lista.length * 3}</span>
          </p>
        </div>
        <div className="mr-2 h-2 w-24 flex-none overflow-hidden rounded-full bg-purple-950 sm:w-32">
          <div className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-300 transition-all duration-700"
            style={{ width: `${Math.min(100, (cleared.size / Math.max(1, lista.length)) * 100)}%` }} />
        </div>
        {!cidade && onClose && (
          <button onClick={onClose} data-testid="gb-world-close"
            className="flex-none rounded-full border border-purple-700/60 bg-black/60 p-2.5 text-purple-100 backdrop-blur-sm transition-colors hover:border-orange-500/70 hover:text-orange-300">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="absolute inset-0">
        <GbFit testid="gb-world-fit" foco={focoMapa}>
          <img src="/gb/world/bg.webp" alt="Floresta Encantada" draggable="false"
            className="absolute inset-0 h-full w-full object-cover" />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_58%,rgba(9,5,18,.6)_100%)]" />

          <svg viewBox="0 0 100 100" preserveAspectRatio="none"
            className="pointer-events-none absolute inset-0 h-full w-full">
            <defs>
              {trechos.map((t, i) => (t.aberto && t.novo ? (
                <mask key={i} id={`gbwr-${i}`} maskUnits="userSpaceOnUse">
                  <path d={t.d} fill="none" stroke="#fff" strokeWidth="6" strokeLinecap="butt"
                    pathLength="100" strokeDasharray="100 100" strokeDashoffset="100">
                    <animate attributeName="stroke-dashoffset" from="100" to="0" dur="2.2s" fill="freeze" />
                  </path>
                </mask>
              ) : null))}
            </defs>
            {trechos.map((t, i) => {
              if (!t.aberto && !t.proximo) return null;
              return (
                <path key={i} d={t.d} fill="none" strokeLinecap="round"
                  mask={t.aberto && t.novo ? `url(#gbwr-${i})` : undefined}
                  strokeWidth={t.aberto ? 0.7 : 0.5}
                  strokeDasharray={t.aberto ? '0.2 2.6' : '0.2 3.2'}
                  stroke={t.aberto ? '#ffc25c' : 'rgba(216,200,245,.35)'}
                  style={t.aberto ? { filter: 'drop-shadow(0 0 2.5px rgba(255,176,32,.85))' } : undefined} />
              );
            })}
          </svg>

          {grupos.map((g) => (
            <CityPin key={g.cidade.id} cidade={g.cidade} fases={g.fases} atual={atual}
              starsCity={starsDe(g.fases)} onOpen={setCidade} />
          ))}
          {(() => {
            const ci = Math.min(CIDADES.length - 1, Math.floor((atual - 1) / 4));
            return <GbTank from={ci > 0 ? CIDADES[ci - 1] : null} to={CIDADES[ci]} size={46} />;
          })()}
        </GbFit>
      </div>

      <div className="absolute inset-x-0 bottom-0 z-20 flex flex-wrap items-center justify-center gap-3 bg-gradient-to-t from-[#0b0712] via-[#0b0712bb] to-transparent px-4 pb-2 pt-6 text-[10px] text-purple-200/80">
        {travado ? (
          <span className="flex items-center gap-1.5 rounded-full border border-amber-500/70 bg-amber-950/70 px-3 py-1.5 text-[11px] font-bold text-amber-100"
            data-testid="gb-world-maintenance">
            <Wrench className="h-3.5 w-3.5 text-amber-400" />
            {avisoTrava || 'Manutenção programada: as fases voltam em breve.'}
          </span>
        ) : (
          <>
            <span>toque na cidade para ver as fases</span>
            <span className="text-purple-300/50">· trilha acesa = caminho já conquistado</span>
          </>
        )}
      </div>

      {cidade && (() => {
        const i = CIDADES.findIndex((c) => c.id === cidade.id);
        const g = grupos.find((x) => x.cidade.id === cidade.id);
        return (
          <GbCityMap cidade={cidade} index={i} fases={g.fases} atual={atual} stars={stars} recem={recem}
            onPick={(n) => setSel(n)} onClose={() => setCidade(null)} />
        );
      })()}

      <GbNodeSheet node={sel} stars={Number(stars[String(sel?.node)] || 0)}
        paid={Number(paid[String(sel?.node)] || 0)} locked={sel ? bloqueada(sel) : true}
        diamonds={diamonds} starting={starting}
        travado={!!travado} avisoTrava={avisoTrava}
        onPlay={async (n) => { await onPlay(n); setSel(null); }}
        onClose={() => setSel(null)} />
    </div>
  );
};
